import * as z from "zod";

import { CalendarSelector, DropdownSelect } from "components/form-control";
import { Decree } from "types/decree.type";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import type { ExtensionInput, UpdatePermitDecreeInput, UpdateTemporaryDecreeInput } from "@/types/gql/graphql";
import { permitDecreeTypes, temporaryDecreeTypes } from "@/modules/restriction/restriction.const";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DecreeCategory } from "@/types/gql/graphql";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

const decreeSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    category: z.string().optional(),
    decreeNumber: z.string().optional(),
    comments: z.string().optional(),
    publishedDate: z.union([z.date(), z.string(), z.null()]).optional(),
    type: z.string().optional(),
    startDate: z.union([z.date(), z.string(), z.null()]).optional(),
    endDate: z.union([z.date(), z.string(), z.null()]).optional(),
    isExtension: z.boolean().optional(),
    extensionReference: z.string().optional(),
    extensionNumber: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.category === DecreeCategory.Permit) {
      if (!data.startDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["startDate"],
          message: "Start Date is required for a permit decree",
        });
      }
      if (!data.endDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["endDate"],
          message: "End Date is required for a permit decree",
        });
      }
    }

    if (data.category === DecreeCategory.Temporary && data.isExtension) {
      if (!data.extensionReference?.trim() && !data.extensionNumber?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["extensionReference"],
          message: "Renseignez la référence ou le numéro de l'arrêté prorogé.",
        });
      }
    }
  });

type DecreeFormValues = z.infer<typeof decreeSchema>;

type EditDecreeProps = {
  decreeData: Decree;
  closeModal: () => void;
  updateDecreeFile: (input: UpdateTemporaryDecreeInput | UpdatePermitDecreeInput) => Promise<void>;
};

export const EditDecreeModal = ({ decreeData, closeModal, updateDecreeFile }: EditDecreeProps) => {
  const form = useForm<DecreeFormValues>({
    resolver: zodResolver(decreeSchema),
    mode: "all",
    defaultValues: {
      name: decreeData.name,
      category: decreeData?.category ?? DecreeCategory.Unknown,
      decreeNumber: decreeData?.decreeNumber ?? "",
      comments: decreeData?.comments ?? "",
      publishedDate: decreeData?.publishedDate ?? null,
      type: decreeData?.type ?? undefined,
      startDate: decreeData?.startDate ?? null,
      endDate: decreeData?.endDate ?? null,
      isExtension: Boolean(
        decreeData?.extension?.originalDecreeReference || decreeData?.extension?.originalDecreeNumber,
      ),
      extensionReference: decreeData?.extension?.originalDecreeReference ?? "",
      extensionNumber: decreeData?.extension?.originalDecreeNumber ?? "",
    },
  });

  const isExtension = form.watch("isExtension");

  const saveDecree = async (data: DecreeFormValues) => {
    const baseDecree = {
      id: decreeData.id,
      name: data.name,
      done: decreeData?.done,
      category: data.category as DecreeCategory,
      decreeNumber: data.decreeNumber?.trim() || null,
      comments: data.comments,
      publishedDate: data.publishedDate ? new Date(data.publishedDate) : undefined,
    };

    let newDecree: UpdateTemporaryDecreeInput | UpdatePermitDecreeInput;

    switch (data.category) {
      case DecreeCategory.Temporary: {
        const hadExtension =
          decreeData.extension?.originalDecreeReference != null || decreeData.extension?.originalDecreeNumber != null;

        let extension: ExtensionInput | undefined;
        if (data.isExtension) {
          extension = {
            originalDecreeReference: data.extensionReference?.trim() || null,
            originalDecreeNumber: data.extensionNumber?.trim() || null,
          };
        } else if (hadExtension) {
          extension = { originalDecreeReference: null, originalDecreeNumber: null };
        }

        newDecree = {
          ...baseDecree,
          type: data.type,
          ...(extension !== undefined && { extension }),
        } as UpdateTemporaryDecreeInput;
        break;
      }
      case DecreeCategory.Permit:
        newDecree = {
          ...baseDecree,
          type: data.type,
          startDate: new Date(data.startDate!),
          endDate: new Date(data.endDate!),
        } as UpdatePermitDecreeInput;
        break;
      default:
        newDecree = baseDecree;
        break;
    }

    await updateDecreeFile(newDecree);
    closeModal();
  };

  const { showTypeDropdown, typeList } = useMemo(() => {
    let typeList: { value: string; label: string }[] = [];
    const showTypeDropdown =
      decreeData.category === DecreeCategory.Temporary || decreeData.category === DecreeCategory.Permit;

    switch (decreeData.category) {
      case DecreeCategory.Temporary:
        typeList = temporaryDecreeTypes;
        break;
      case DecreeCategory.Permit:
        typeList = permitDecreeTypes;
        break;
    }

    return { showTypeDropdown, typeList };
  }, [decreeData.category]);

  return (
    <Dialog open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit decree</DialogTitle>
          <DialogDescription>Decree information</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(saveDecree)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="decreeNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Numéro de l'arrêté</FormLabel>
                  <FormControl>
                    <Input placeholder="ex. 2024-456" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {showTypeDropdown && (
              <FormField
                control={form.control}
                name="type"
                render={({ field: { onChange, value } }) => (
                  <FormItem>
                    <FormLabel>
                      Type<span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <DropdownSelect value={value} options={typeList} onValueChange={onChange} className="w-full" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {decreeData.category === DecreeCategory.Temporary && (
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="isExtension"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center gap-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked === true)}
                        />
                      </FormControl>
                      <FormLabel className="font-normal">Cet arrêté proroge un arrêté antérieur</FormLabel>
                    </FormItem>
                  )}
                />

                {isExtension && (
                  <>
                    <FormField
                      control={form.control}
                      name="extensionReference"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Référence de l'arrêté prorogé</FormLabel>
                          <FormControl>
                            <Input placeholder="ex. arrêté n°2024-123 du 15/03/2024" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="extensionNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Numéro de l'arrêté prorogé</FormLabel>
                          <FormControl>
                            <Input placeholder="ex. 2024-123" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
              </div>
            )}

            {decreeData.category === DecreeCategory.Permit && (
              <>
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Start Date<span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <CalendarSelector selectedDate={field.value} onChangeValue={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        End Date<span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <CalendarSelector selectedDate={field.value} onChangeValue={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <FormField
              control={form.control}
              name="comments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Comment</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Type your message here." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="publishedDate"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Published Date</FormLabel>
                  <FormControl>
                    <CalendarSelector selectedDate={value ?? undefined} onChangeValue={onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="sm:justify-between">
              <Button onClick={closeModal} variant="secondary" type="button">
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
